import React, { useState, useEffect } from 'react';

import DialogContent from '@mui/material/DialogContent';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

import { ReclamationActionDetailType, ReclamationActionDetailCodes, MotifResolutionCodes, CausePrincipaleCodes } from 'constants.js';

import PieceLookup from 'components/Shared/Piece/PieceLookup';
import PieceApercu from 'components/Shared/Piece/PieceApercu';
import { ApiPieceAddAction, ApiPieceGestionLigne } from 'types/apiReturns';

type ActionDetail = {
    code: string;
    type: number;
    libelle: string;
    position: number;
    valeurString?: string;
};

type ActionData = {
    code: string;
    details: ActionDetail[];
};

// Données de clôture du dossier courant, enchaînée par le container après l'action
// (2e appel à l'endpoint cloturer, qui pose le CRM et le mail de clôture).
type ClotureData = {
    motifResolutionCode: string;
    causePrincipaleCode: string;
    causePrincipaleDetailId: number;
    utilisateurFautifId: number;
    commentaire: string;
    mailAutomatique: string;
    fichiersIds: number[];
};

type Props = {
    onChange: (actionData: ActionData | null, clotureData?: ClotureData | null) => void;
    action: { code: string };
    isAdding?: boolean;
};

// Action « Dossier lié manuel » : à partir d'une pièce de retour validée (préfixe « L »),
// transmet au GT Traitement des retours un dossier permettant le traitement de l'avoir client.
// Layout 2 colonnes (formulaire 40 % / aperçu pièce informatif 60 %).
export default function DossierLieManuelDialogContent({ onChange, action, isAdding = false }: Props) {

    const [piece, setPiece] = useState<ApiPieceAddAction | null>(null);
    const [lignes, setLignes] = useState<ApiPieceGestionLigne[]>([]);
    const [commentaire, setCommentaire] = useState('');

    // Recalcule actionData + clotureData à chaque changement d'état et les remonte au parent.
    // Renvoie null (bouton désactivé) tant que la pièce n'est pas valide.
    useEffect(() => {

        if (piece === null) {
            onChange(null, null);
            return;
        }

        const details: ActionDetail[] = [
            {
                code: ReclamationActionDetailCodes.NumeroPiece,
                type: ReclamationActionDetailType.LienGeswebBonLivraison,
                libelle: 'Pièce à utiliser pour le retour',
                position: 0,
                valeurString: piece.numeroPiece
            }
        ];

        if (commentaire.trim() !== '') {
            details.push({
                code: ReclamationActionDetailCodes.Commentaire,
                type: ReclamationActionDetailType.Texte,
                libelle: 'Commentaire',
                position: details.length,
                valeurString: commentaire.trim()
            });
        }

        // Clôture du dossier courant enchaînée après l'action : motif et cause figés.
        // L'endpoint cloturer (2e appel du container) pose le CRM Client/Cession et le mail.
        const clotureData: ClotureData = {
            motifResolutionCode: MotifResolutionCodes.RegularisationFacturationClient,
            causePrincipaleCode: CausePrincipaleCodes.RetourMarchandises,
            causePrincipaleDetailId: 0,
            utilisateurFautifId: 0,
            commentaire: '',
            mailAutomatique: '',
            fichiersIds: []
        };

        onChange({
            code: action.code,
            details
        }, clotureData);

    }, [piece, commentaire, action.code, onChange]);

    const pieceValidee = piece !== null;

    return (
        <DialogContent>
            <Box sx={{ display: 'flex', gap: 2, mt: 1 }}>

                {/* Colonne gauche : formulaire (40 %) */}
                <Box sx={{ flex: '0 0 40%', display: 'flex', flexDirection: 'column', gap: 2 }}>

                    <Typography variant="body2" color="textSecondary">
                        Transmettre au GT Retour un dossier permettant le traitement de l'avoir client.
                    </Typography>

                    <PieceLookup
                        label="Pièce à utiliser pour le retour"
                        prefixeLObligatoire
                        disabled={isAdding}
                        onPieceValidee={(p, l) => {
                            setPiece(p);
                            setLignes(l);
                        }}
                    />

                    {pieceValidee && (
                        <TextField
                            label="Commentaire"
                            value={commentaire}
                            onChange={event => setCommentaire(event.target.value)}
                            multiline
                            fullWidth
                            disabled={isAdding}
                        />
                    )}
                </Box>

                {/* Colonne droite : aperçu informatif de la pièce (60 %) */}
                <Box sx={{ flex: '1 1 60%' }}>
                    <PieceApercu piece={piece} lignes={lignes} />
                </Box>

            </Box>
        </DialogContent>
    );
}
